import { formatRating } from "../lib/format.js";

interface RatingProps {
  value: number | null;
  size?: "sm" | "md" | "lg";
}

const SIZES = {
  sm: { text: "text-sm", star: "h-3 w-3", gap: "gap-0.5" },
  md: { text: "text-base", star: "h-4 w-4", gap: "gap-1" },
  lg: { text: "text-lg", star: "h-5 w-5", gap: "gap-1" },
} as const;

/**
 * Rating shown as five stars (a /10 score halved) alongside the numeric value.
 *
 * The stars are decorative — `aria-hidden` — and the accessible name carries
 * the actual number, so a screen reader hears "8.5 out of 10" rather than
 * counting glyphs.
 */
export function Rating({ value, size = "md" }: RatingProps) {
  const formatted = formatRating(value);
  if (formatted === null || value === null) return null;

  const styles = SIZES[size];
  const outOfFive = value / 2;

  return (
    <span
      className={`inline-flex items-center ${styles.gap} ${styles.text}`}
      aria-label={`Rated ${formatted} out of 10`}
    >
      <span className={`flex ${styles.gap}`} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((index) => {
          const fill = Math.max(0, Math.min(1, outOfFive - index));

          return (
            <span key={index} className="relative inline-block">
              <Star className={`${styles.star} text-ink-500`} />
              {fill > 0 && (
                <span
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: `${fill * 100}%` }}
                >
                  <Star className={`${styles.star} text-lamp-400`} />
                </span>
              )}
            </span>
          );
        })}
      </span>

      <span className="ml-1 font-semibold tabular-nums text-paper-50">
        {formatted}
      </span>
      <span className="text-paper-500">/10</span>
    </span>
  );
}

export function Star({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2.5l2.9 6.06 6.6.9-4.8 4.62 1.19 6.62L12 17.6l-5.89 3.1 1.19-6.62-4.8-4.62 6.6-.9L12 2.5z" />
    </svg>
  );
}
